import React, { useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useGetAuthenticatedUser } from '../../../Flux/Selector/index.js'
import { isGlobalAdmin } from '../../../Model/User/IUser.js'
import { StatsBarChart } from '../AdminStatsPage/StatsBarChart.js'
import { Card } from '../AdminStatsPage/StatsUi.js'
import { SpinChrome } from './SpinChrome.js'
import { InfoTip } from './SpinUi.js'
import { fanoutData } from './spinMetrics.logic.js'
import { useSelectedRun } from './useSelectedRun.js'
import { useSpinCompare } from './useSpinData.js'

export const SpinComparePage = () => {
  const user = useGetAuthenticatedUser()
  const sel = useSelectedRun()
  const { runs, selected, amplicons } = sel

  const algorithms = useMemo(() => [...new Set(runs.map((r) => r.algorithmType))], [runs])
  const [cmpA, setCmpA] = useState('')
  const [cmpB, setCmpB] = useState('')
  const [cmpAmplicon, setCmpAmplicon] = useState('')
  const effA = algorithms.includes(cmpA) ? cmpA : (algorithms[0] ?? '')
  const effB = algorithms.includes(cmpB) ? cmpB : (algorithms.find((x) => x !== effA) ?? '')
  // A run (hence its current set) is always for a single amplicon, so compare is scoped to one —
  // never "all amplicons". Default to the amplicon currently in view, then the first available.
  // Kept page-local (not the shared `tax` param), since "all" is never valid here.
  const effCmpAmplicon = amplicons.includes(cmpAmplicon)
    ? cmpAmplicon
    : selected?.amplicon && amplicons.includes(selected.amplicon)
      ? selected.amplicon
      : (amplicons[0] ?? '')
  const canCompare = algorithms.length >= 2 && effCmpAmplicon !== ''
  const { compare } = useSpinCompare(canCompare ? effA : undefined, canCompare ? effB : undefined, effCmpAmplicon)

  if (!user) return <></>
  if (!isGlobalAdmin(user)) return <Navigate to="/home" replace />

  return (
    <div className="space-y-8">
      <SpinChrome title="SPIN · Compare" sel={sel} />

      {!canCompare && (
        <p className="text-gray-500">Comparison needs at least two algorithms and one amplicon.</p>
      )}
      {canCompare && compare && (
        <section className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-semibold">
              Algorithm comparison
              <InfoTip tip="Do the two algorithms group OTUs the same way? Since they use unrelated SPIN ids, agreement is measured structurally — over the OTUs both placed, does each algorithm's cluster stay together in the other?" />
            </h2>
            <select className="select select-bordered select-xs" value={effA} onChange={(e) => setCmpA(e.target.value)}>
              {algorithms.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
            <span className="text-gray-500">vs</span>
            <select className="select select-bordered select-xs" value={effB} onChange={(e) => setCmpB(e.target.value)}>
              {algorithms.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
            {amplicons.length > 0 && (
              <>
                <span className="text-gray-500">on</span>
                <select
                  className="select select-bordered select-xs"
                  value={effCmpAmplicon}
                  onChange={(e) => setCmpAmplicon(e.target.value)}
                >
                  {amplicons.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </>
            )}
          </div>

          <Card title="Summary (each as of its latest run)">
            <div className="overflow-x-auto">
              <table className="table table-xs">
                <thead>
                  <tr>
                    <th></th>
                    <th className="text-right">{compare.a.algorithmType}</th>
                    <th className="text-right">{compare.b.algorithmType}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>SPINs</td>
                    <td className="text-right">{compare.a.spinCount.toLocaleString()}</td>
                    <td className="text-right">{compare.b.spinCount.toLocaleString()}</td>
                  </tr>
                  <tr>
                    <td>
                      Member OTUs
                      <InfoTip tip="Distinct OTUs this algorithm currently has in a SPIN. These two totals can differ — the algorithms need not cover the same OTU universe." />
                    </td>
                    <td className="text-right">{compare.a.memberCount.toLocaleString()}</td>
                    <td className="text-right">{compare.b.memberCount.toLocaleString()}</td>
                  </tr>
                  <tr>
                    <td>
                      Avg members / SPIN
                      <InfoTip tip="Member OTUs ÷ SPINs — a per-algorithm average cluster size, NOT the shared count. A higher value means fewer, coarser clusters." />
                    </td>
                    <td className="text-right">{(compare.a.memberCount / (compare.a.spinCount || 1)).toFixed(1)}</td>
                    <td className="text-right">{(compare.b.memberCount / (compare.b.spinCount || 1)).toFixed(1)}</td>
                  </tr>
                  <tr>
                    <td>
                      Shared OTUs (both clustered)
                      <InfoTip tip="OTUs that BOTH algorithms placed in a SPIN — the intersection. All concordance below is computed over exactly these. It equals each Member-OTUs total only if both cover the identical OTU set." />
                    </td>
                    <td className="text-right" colSpan={2}>
                      {compare.sharedOtus.toLocaleString()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card title={`Concordance: ${compare.a.algorithmType} SPIN → # distinct ${compare.b.algorithmType} SPINs`}>
              <StatsBarChart data={fanoutData(compare.fanoutAtoB ?? [])} />
              <p className="text-xs text-gray-500 mt-2">
                For each {compare.a.algorithmType} cluster, how many different {compare.b.algorithmType} clusters its shared members fall
                into. <b>1 = intact</b> ({compare.b.algorithmType} keeps it in one piece); &gt;1 = {compare.b.algorithmType} splits it
                apart. Bars weighted toward 1 ⇒ the two agree.
              </p>
            </Card>
            <Card title={`Concordance: ${compare.b.algorithmType} SPIN → # distinct ${compare.a.algorithmType} SPINs`}>
              <StatsBarChart data={fanoutData(compare.fanoutBtoA ?? [])} />
              <p className="text-xs text-gray-500 mt-2">
                The reverse direction — splitting isn&rsquo;t symmetric, so both charts are needed. <b>1 = intact</b> in{' '}
                {compare.a.algorithmType}; &gt;1 = {compare.a.algorithmType} breaks that {compare.b.algorithmType} cluster across several.
              </p>
            </Card>
          </div>
        </section>
      )}
    </div>
  )
}
